import { existsSync } from "fs";
import { resolve } from "path";

import { serveStatic } from "hono/bun";
import type { CronScheduleDef, GencowAuthConfig, GencowConfig, GencowProcedureDef } from "@gencow/core";
import {
  GencowValidationError,
  getQueryDef,
  getRegisteredHttpActions,
  getRegisteredMutations,
  getRegisteredQueries,
  getRegisteredWorkflows,
  getWorkflowResumeActionName,
} from "@gencow/core";
import { storageRoutes, storageUploadRoutes } from "./storage.js";
import { collectAdminFunctionDetails } from "./admin-function-details.js";
import { createScheduler } from "./scheduler.js";
import { collectProcedureRouteMaps, mergeProcedureRouteMaps } from "./procedure-route-maps.js";
import { collectRuntimeOperationInventory } from "./runtime-operation-inventory.js";
import { buildActionTokenMap } from "./cron-action-tokens.js";
import { collectCronSchedules, hasDefineApiCrons } from "./cron-schedule-collect.js";
import { collectHttpRoutesFromApi } from "./http-route-collect.js";
import { collectOwnerRlsTables } from "./crud-route-maps.js";

import { warnIfPlaceholderAiKey } from "./server-ai-runtime.js";
import { registerInternalCronRoute } from "./server-internal-cron-route.js";
import { registerDeclarativeCronJobs } from "./server-declarative-crons.js";
import { registerHttpActions } from "./server-http-actions.js";
import {
  logFunctionSecuritySummary,
  logOwnerRlsSummary,
  logRegisteredRpcEndpoints,
  registerAdminSurfaceRoutes,
  resolveAdminSurfaceAccess,
  resolveStorageTierConfig,
  summarizeFunctionSecurity,
} from "./server-api-surface.js";
import {
  createShutdownHandler,
  registerShutdownSignals,
  startUsageMeteringFlush,
} from "./server-process-runtime.js";
import { createRequestContextBuilder } from "./server-request-context.js";
import { registerRealtimeChannelRoute } from "./server-realtime-channel-route.js";
import { registerRealtimeOutboxDiagnosticsRoute } from "./server-realtime-outbox-diagnostics-route.js";
import { registerRealtimeQueryRoute } from "./server-realtime-query-route.js";
import { registerRealtimeTicketRoute } from "./server-realtime-ticket-route.js";
import { registerRagIngestRuntime } from "./rag-ingest-runtime.js";
import { collectBuiltinProcedureMaps } from "./builtins/index.js";
import { isCloudFeatureEnabled } from "./cloud-features.js";
import { buildApiRouter } from "./server-rpc-routes.js";
import {
  registerSchedulerMutationActions,
  registerWorkflowResumeActions,
} from "./server-scheduler-bootstrap.js";
import { loadWorkflowV2CapacityPolicyFromEnv } from "./workflow-kernel-v2-capacity.js";
import {
  resolveWorkflowV2ReconcilerIntervalMs,
  startWorkflowV2Reconciler,
} from "./workflow-kernel-v2-reconciler.js";
import {
  assertBaasPortEnv,
  buildRootStatusPayload,
  buildServerStartupBanner,
  createDashboardWebSocketHandlers,
  createReadinessHandler,
  ensureInternalUrl,
  loadCloudDashboardState,
  registerCloudDashboardSpa,
  registerEmbeddedDashboardRoutes,
} from "./server-shell-runtime.js";
import { readShutdownReasonFromMarker } from "./server-lifecycle.js";
import { subscribeToLogs, unsubscribeFromLogs } from "./admin";
import { createHttpGatewayFanout } from "./realtime/http-gateway-fanout.js";
import { createLocalRealtimeFanout, type LocalRealtimeRegistry } from "./realtime/local-fanout.js";
import {
  createRealtimeOutboxFanout,
  createRealtimeOutboxWorker,
  resolveRealtimeOutboxPollIntervalMs,
  startRealtimeOutboxWakeBusListener,
} from "./realtime/realtime-outbox.js";
import { createPostgresWakeSignalRuntime } from "./wake-signal/postgres-wake-signal-runtime.js";
import { GENCOW_APP_WAKE_CHANNEL } from "./wake-signal/wake-signal-types.js";

type ServerDriver = "pglite" | "postgres";
type GatewayNotifier = (payload: Record<string, unknown>) => void;

function isLocalRealtimeRegistry(value: unknown): value is LocalRealtimeRegistry {
  return !!value && typeof value === "object" && "handleWsMessage" in value;
}

export function shouldAutoCreateScheduler(params: {
  isBaas: boolean;
  hasCrons: boolean;
  hasDefineApiCrons: boolean;
  hasWorkflows: boolean;
}): boolean {
  return params.isBaas || params.hasCrons || params.hasDefineApiCrons || params.hasWorkflows;
}

export function buildBunFetchEnv(server: any, req: Request): Record<string, unknown> {
  return {
    server,
    ip: typeof server?.requestIP === "function" ? server.requestIP(req) : undefined,
  };
}

export function shouldUsePlatformSystemDbForHttpActionPath(path: string): boolean {
  return (
    path.startsWith("/internal/") ||
    path.startsWith("/platform/ai/") ||
    path.startsWith("/platform/document/") ||
    path.startsWith("/platform/domain/")
  );
}

export function collectTransitionCronProcedureNames(
  cronSchedules: readonly CronScheduleDef[],
  internalProcedureMap: Map<string, GencowProcedureDef<"internal", any, any, any>>,
): string[] {
  return [
    ...new Set(
      cronSchedules
        .map((schedule) => schedule.procedureName)
        .filter((procedureName) => !internalProcedureMap.has(procedureName)),
    ),
  ];
}

async function ensureScheduler(params: {
  scheduler: any;
  isBaas: boolean;
  appName: string;
  internalToken: string;
  functionsPath: string;
  hasDefineApiCrons: boolean;
  fetchPlatformInternal: (path: string, init: RequestInit, label: string) => Promise<Response>;
  logger: Pick<Console, "log">;
}): Promise<any> {
  let scheduler = params.scheduler;
  scheduler?.start?.();
  if (scheduler) return scheduler;

  const cronsPath = resolve(params.functionsPath, "crons.ts");
  const cronsPathJs = resolve(params.functionsPath, "crons.js");
  const hasCrons = existsSync(cronsPath) || existsSync(cronsPathJs);
  const hasWorkflows = getRegisteredWorkflows().length > 0;

  if (
    !shouldAutoCreateScheduler({
      isBaas: params.isBaas,
      hasCrons,
      hasDefineApiCrons: params.hasDefineApiCrons,
      hasWorkflows,
    })
  ) {
    return scheduler;
  }

  if (params.isBaas) {
    scheduler = createScheduler({
      persistFailureMode: "fail-closed",
      persistJob: async (job) => {
        const res = await params.fetchPlatformInternal(
          "/internal/scheduler/persist",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Internal-Token": params.internalToken,
            },
            body: JSON.stringify({
              appName: params.appName,
              id: job.id,
              action: job.action,
              args: job.args,
              runAt: job.runAt.toISOString(),
              onErrorAction: job.onErrorAction ?? null,
              dedupeKey: job.dedupeKey ?? null,
            }),
          },
          "scheduler-persist",
        );
        if (!res.ok) {
          const errBody = await res.text().catch(() => "");
          throw new Error(`[scheduler] persist failed: ${res.status} ${errBody}`);
        }
      },
      removeJob: async (jobId) => {
        const res = await params.fetchPlatformInternal(
          "/internal/scheduler/cancel",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Internal-Token": params.internalToken,
            },
            body: JSON.stringify({ appName: params.appName, id: jobId }),
          },
          "scheduler-cancel",
        );
        if (!res.ok) return false;
        const data = (await res.json()) as { deleted?: boolean };
        return !!data.deleted;
      },
    });
    params.logger.log("[gencow] Durable scheduler created (Platform DB-backed, sleep-safe)");
    return scheduler;
  }

  scheduler = createScheduler();
  params.logger.log(
    "[gencow] Auto-created scheduler for defineApi.crons/crons.ts/workflow() (in-memory mode)",
  );
  return scheduler;
}

export async function bootstrapServerMainRuntime(params: {
  app: any;
  appModule: any;
  upgradeWebSocket: ReturnType<typeof import("hono/bun").createBunWebSocket>["upgradeWebSocket"];
  websocket: any;
  db: any;
  systemDb?: any;
  platformSystemDb?: any;
  rawSql: any;
  analyticsRawSql?: any;
  close: () => Promise<void>;
  driver: ServerDriver;
  storage: any;
  storageDir: string;
  settingsRoutes: any;
  functionsPath: string;
  isBaas: boolean;
  isPlatform: boolean;
  appName: string;
  internalToken: string;
  authConfig?: GencowAuthConfig;
  projectConfig?: GencowConfig;
  executeWithTimeout: typeof import("./server-execution.js").executeWithTimeout;
  parseArgs: typeof import("@gencow/core").parseArgs;
  runtimeBuffer: import("./platform-failover.js").RuntimeBuffer;
  fetchPlatformInternal: (path: string, init: RequestInit, label: string) => Promise<Response>;
  notifyGateway: GatewayNotifier;
  readShutdownReasonFromMarker: typeof readShutdownReasonFromMarker;
  wsTimers: WeakMap<object, number>;
  wakeSignalEnv?: Record<string, string | undefined>;
  realtimeOutboxEnv?: Record<string, string | undefined>;
  workflowReconcilerEnv?: Record<string, string | undefined>;
  logger?: Console;
}): Promise<ReturnType<typeof Bun.serve>> {
  const logger = params.logger ?? console;
  const httpGatewayFanout = createHttpGatewayFanout({
    notifyGateway: (event) => params.notifyGateway(event),
    notifyGatewayAsync: async (event) => {
      const res = await params.fetchPlatformInternal(
        "/internal/realtime-notify",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Internal-Token": params.internalToken,
          },
          body: JSON.stringify({ appName: params.appName, ...event }),
        },
        "realtime-notify",
      );
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(`Gateway notify failed: ${res.status} ${body.slice(0, 160)}`);
      }
    },
  });
  const wakeSignalRuntime = params.isBaas
    ? await createPostgresWakeSignalRuntime({
        rawSql: params.rawSql,
        env: params.wakeSignalEnv,
        logger,
        defaultChannel: GENCOW_APP_WAKE_CHANNEL,
      }).catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);
        logger.warn(`[wake-signal] App realtime listener runtime disabled: ${message.slice(0, 160)}`);
        return null;
      })
    : null;
  const realtimeOutboxWorker = params.isBaas
    ? createRealtimeOutboxWorker({
        rawSql: params.rawSql,
        fanout: httpGatewayFanout,
        intervalMs: resolveRealtimeOutboxPollIntervalMs(params.realtimeOutboxEnv ?? process.env),
        logger,
      })
    : null;
  const realtimeOutboxWakeUnsubscribe =
    params.isBaas && wakeSignalRuntime && realtimeOutboxWorker
      ? await startRealtimeOutboxWakeBusListener({
          bus: wakeSignalRuntime.bus,
          appName: params.appName,
          wakeWorker: () => realtimeOutboxWorker.wake(),
          logger,
        }).catch((error: unknown) => {
          const message = error instanceof Error ? error.message : String(error);
          logger.warn(`[realtime-outbox] wake listener disabled: ${message.slice(0, 160)}`);
          return null;
        })
      : null;
  const realtimeFanout = params.isBaas
    ? createRealtimeOutboxFanout({
        rawSql: params.rawSql,
        appName: params.appName,
        inner: httpGatewayFanout,
        logger,
        wakeWorker: () => realtimeOutboxWorker?.wake() ?? Promise.resolve(),
        postgresWakeSignal: wakeSignalRuntime ? { channel: GENCOW_APP_WAKE_CHANNEL } : undefined,
      })
    : createLocalRealtimeFanout({
        appName: params.appName,
        channelTokenSecret: params.internalToken,
      });

  if (isCloudFeatureEnabled(params.projectConfig, "rag")) {
    registerRagIngestRuntime();
  }

  params.appModule.setDb?.(params.db);
  const appHasDefineApiCrons = hasDefineApiCrons(params.appModule);
  const scheduler = await ensureScheduler({
    scheduler: params.appModule.scheduler,
    isBaas: params.isBaas,
    appName: params.appName,
    internalToken: params.internalToken,
    functionsPath: params.functionsPath,
    hasDefineApiCrons: appHasDefineApiCrons,
    fetchPlatformInternal: params.fetchPlatformInternal,
    logger,
  });

  const {
    queryProcedureMap: appQueryProcedureMap,
    mutationProcedureMap: appMutationProcedureMap,
    internalProcedureMap,
  } = collectProcedureRouteMaps(params.appModule);
  const { queryProcedureMap, mutationProcedureMap } = mergeProcedureRouteMaps(
    {
      queryProcedureMap: appQueryProcedureMap,
      mutationProcedureMap: appMutationProcedureMap,
    },
    collectBuiltinProcedureMaps(params.projectConfig),
    { logger },
  );
  const cronSchedules = collectCronSchedules(params.appModule);
  const unresolvedCronProcedureNames = collectTransitionCronProcedureNames(
    cronSchedules,
    internalProcedureMap,
  );
  if (unresolvedCronProcedureNames.length > 0) {
    logger.warn(
      `[crons] ${unresolvedCronProcedureNames.length} schedule(s) use transition procedure lookup instead of procedure.internal: ${unresolvedCronProcedureNames.join(", ")}`,
    );
  }
  const deployId = process.env.GENCOW_DEPLOY_ID?.trim() || "local-dev";
  const actionTokenMap = buildActionTokenMap({
    cronSchedules,
    internalProcedureMap,
    deployId,
    allowTransitionProcedureNames: true,
  });

  const buildCtx = createRequestContextBuilder({
    db: params.db,
    systemDb: params.systemDb,
    rawSql: params.rawSql,
    analyticsRawSql: params.analyticsRawSql ?? params.rawSql,
    storage: params.storage,
    scheduler,
    isBaas: params.isBaas,
    isPlatform: params.isPlatform,
    appName: params.appName,
    internalToken: params.internalToken,
    authConfig: params.authConfig,
    realtimeFanout,
    deferRealtimeFanout: params.isBaas,
    procedureQueryMap: queryProcedureMap,
  });

  registerRealtimeTicketRoute({
    app: params.app,
    appName: params.appName,
    internalToken: params.internalToken,
    buildCtx,
  });
  registerRealtimeChannelRoute({
    app: params.app,
    appName: params.appName,
    internalToken: params.internalToken,
    buildCtx,
  });
  registerRealtimeQueryRoute({
    app: params.app,
    appName: params.appName,
    internalToken: params.internalToken,
    buildCtx,
  });

  const mutations = getRegisteredMutations();
  const mutationMap = new Map(mutations.map((mutation) => [mutation.name, mutation]));

  if (scheduler) {
    registerSchedulerMutationActions({
      scheduler,
      mutationMap,
      internalProcedureMap,
      mutationProcedureMap,
      actionTokenMap,
      buildCtx: () => buildCtx({ get: () => null }),
      parseArgs: params.parseArgs,
      executeWithTimeout: params.executeWithTimeout,
    });
  }

  const workflows = getRegisteredWorkflows();
  if (scheduler && workflows.length > 0) {
    const workflowCapacityPolicy = loadWorkflowV2CapacityPolicyFromEnv(process.env, {
      useDefaults: params.isBaas,
    });
    await registerWorkflowResumeActions({
      scheduler,
      workflows,
      db: params.db,
      buildCtx: () => buildCtx({ get: () => null }),
      executeWithTimeout: params.executeWithTimeout,
      isBaas: params.isBaas,
      workflowCapacityPolicy,
    });
    startWorkflowV2Reconciler({
      db: params.db,
      workflows,
      scheduler,
      getWorkflowResumeActionName,
      logger,
      intervalMs: resolveWorkflowV2ReconcilerIntervalMs(params.workflowReconcilerEnv ?? process.env),
      wakeSignalBus: wakeSignalRuntime?.bus,
      wakeSignalKinds: ["workflow", "rag_ingest"],
    });
  }

  if (scheduler) {
    await registerDeclarativeCronJobs({
      scheduler,
      functionsPath: params.functionsPath,
      appModule: params.appModule,
      mutationMap,
      internalProcedureMap,
      mutationProcedureMap,
      buildCtx: () => buildCtx({ get: () => null }),
      parseArgs: params.parseArgs,
      executeWithTimeout: params.executeWithTimeout,
      isBaas: params.isBaas,
    });
  }

  const apiRouter = buildApiRouter({
    storageRouteHandler: storageRoutes(
      params.storage,
      params.rawSql,
      params.storageDir,
      resolveStorageTierConfig(process.env.__GENCOW_IMAGE_TIER_CONFIG),
      params.isBaas
        ? {
            onImageTransform: (metric) => {
              params.runtimeBuffer.add("image_transforms", metric.transformCount);
              params.runtimeBuffer.add("image_source_bytes", metric.sourceBytes);
              params.runtimeBuffer.add("image_output_bytes", metric.outputBytes);
            },
          }
        : undefined,
      {
        appName: params.appName,
        readGrantSecret: params.internalToken,
        requireReadGrant: process.env.GENCOW_STORAGE_REQUIRE_READ_GRANT === "1",
      },
    ),
    storageUploadRouteHandler: storageUploadRoutes(params.storage),
    getQueryDef,
    mutationMap,
    queryProcedureMap,
    mutationProcedureMap,
    buildCtx,
    executeWithTimeout: params.executeWithTimeout,
    parseArgs: params.parseArgs,
    validationErrorCtor: GencowValidationError,
    isBaas: params.isBaas,
    logger,
  });

  const queryNames = getRegisteredQueries();
  const getOperationInventory = () =>
    collectRuntimeOperationInventory({
      procedureMaps: { queryProcedureMap, mutationProcedureMap },
    });
  const getFunctionDetails = () =>
    collectAdminFunctionDetails({
      procedureMaps: { queryProcedureMap, mutationProcedureMap },
    });
  const operationInventory = getOperationInventory();
  const securitySummary = summarizeFunctionSecurity({
    queryNames,
    getQueryDef,
    mutations,
    procedureQueries: operationInventory.procedureQueries,
    procedureMutations: operationInventory.procedureMutations,
  });

  logRegisteredRpcEndpoints({
    queryNames,
    mutationNames: securitySummary.mutationNames,
    procedureQueryNames: [...queryProcedureMap.keys()],
    procedureMutationNames: [...mutationProcedureMap.keys()],
    logger,
  });
  logFunctionSecuritySummary({
    summary: securitySummary,
    logger,
  });
  logOwnerRlsSummary({
    rlsTables: collectOwnerRlsTables(params.appModule),
    logger,
  });
  warnIfPlaceholderAiKey(process.env, logger);

  params.app.route("/api", apiRouter);

  if (params.isBaas) {
    registerInternalCronRoute({
      app: params.app,
      mutationMap,
      internalProcedureMap,
      mutationProcedureMap,
      actionTokenMap,
      scheduler: scheduler ?? undefined,
      buildCtx: () => buildCtx({ get: () => null }),
      parseArgs: params.parseArgs,
      executeWithTimeout: params.executeWithTimeout,
      internalToken: params.internalToken,
      cronToken: process.env.GENCOW_CRON_TOKEN,
      logger,
    });
    registerRealtimeOutboxDiagnosticsRoute({
      app: params.app,
      rawSql: params.rawSql,
      internalToken: params.internalToken,
      getWakeSignalDiagnostics: wakeSignalRuntime ? () => wakeSignalRuntime.getDiagnostics() : undefined,
      logger,
    });
  }

  // Phase A: merge defineApi({ httpRoutes }) with legacy globalThis registry.
  // Dedupe by (method, path); the collector (defineApi.httpRoutes) wins on collision so that
  // an app-declared httpRoute can override a legacy httpAction registration during migration.
  // See docs/specs/spec-http-route-api.md — "Collision rule" subsection.
  const collectedHttpRoutes = collectHttpRoutesFromApi(params.appModule);
  const collectedKeys = new Set(collectedHttpRoutes.map((a) => `${a.method} ${a.path}`));
  const legacyHttpRoutes = getRegisteredHttpActions();
  const droppedLegacy = legacyHttpRoutes.filter((a) => collectedKeys.has(`${a.method} ${a.path}`));
  const survivingLegacy = legacyHttpRoutes.filter((a) => !collectedKeys.has(`${a.method} ${a.path}`));

  for (const dropped of droppedLegacy) {
    logger.log(
      `[gencow] ⚠️  httpRoute "${dropped.method} ${dropped.path}" overrides a legacy httpAction registration for the same path.`,
    );
  }

  const allHttpRoutes = [...collectedHttpRoutes, ...survivingLegacy];

  registerHttpActions({
    app: params.app,
    actions: allHttpRoutes,
    buildCtx: (context, action) => {
      const ctx = buildCtx(context);
      if (
        params.isPlatform &&
        params.platformSystemDb &&
        shouldUsePlatformSystemDbForHttpActionPath(action.path)
      ) {
        return { ...ctx, db: params.platformSystemDb };
      }
      return ctx;
    },
    executeWithTimeout: params.executeWithTimeout,
    logger,
  });

  registerAdminSurfaceRoutes({
    app: params.app,
    db: params.db,
    rawSql: params.rawSql,
    driver: params.driver,
    buildCtx: () => buildCtx({ get: () => null }),
    storage: params.storage,
    functionsPath: params.functionsPath,
    scheduler,
    settingsRoutes: params.settingsRoutes,
    access: resolveAdminSurfaceAccess({ isBaas: params.isBaas, isPlatform: params.isPlatform }),
    getOperationInventory,
    getFunctionDetails,
  });

  const dashboardEmbedded = await registerEmbeddedDashboardRoutes({
    app: params.app,
    isBaas: params.isBaas,
    isPlatform: params.isPlatform,
    envDashboardDir: process.env.GENCOW_DASHBOARD_DIR,
    importMetaDirname: import.meta.dirname,
    cwd: process.cwd(),
    functionsPath: process.env.GENCOW_FUNCTIONS,
    existsSyncImpl: existsSync,
    serveStaticImpl: serveStatic,
    logger,
  });

  const { dir: cloudDashboardDir, html: cloudDashboardHtml } = loadCloudDashboardState({
    isPlatform: params.isPlatform,
    importMetaDirname: import.meta.dirname,
    cwd: process.cwd(),
    existsSyncImpl: existsSync,
    logger,
  });

  if (isLocalRealtimeRegistry(realtimeFanout)) {
    params.app.get(
      "/ws",
      params.upgradeWebSocket(() =>
        createDashboardWebSocketHandlers({
          isBaas: params.isBaas,
          wsTimers: params.wsTimers,
          runtimeBuffer: params.runtimeBuffer,
          registerClient: (ws) => realtimeFanout.registerClient(ws as never),
          subscribeToLogs,
          unsubscribeFromLogs,
          deregisterClient: (ws) => realtimeFanout.deregisterClient(ws as never),
          handleWsMessage: (ws, message) => realtimeFanout.handleWsMessage(ws as never, message),
        }),
      ),
    );
  }

  params.app.get(
    "/health/ready",
    createReadinessHandler({
      rawSql: params.rawSql,
      getRegisteredQueries,
      getRegisteredMutations,
      getOperationInventory,
    }),
  );

  if (params.isPlatform) {
    registerCloudDashboardSpa({
      app: params.app,
      dir: cloudDashboardDir,
      html: cloudDashboardHtml,
      serveStaticImpl: serveStatic,
    });
  } else {
    params.app.get("/", (c: any) => c.json(buildRootStatusPayload()));
  }

  const port = Number(process.env.PORT || 5456);
  ensureInternalUrl(process.env, port);
  assertBaasPortEnv({ isBaas: params.isBaas, env: process.env, logger });

  let server: ReturnType<typeof Bun.serve>;
  server = Bun.serve({
    port,
    fetch: (req) => params.app.fetch(req, buildBunFetchEnv(server, req)),
    websocket: params.websocket,
  });

  logger.log(
    buildServerStartupBanner({
      port,
      driver: params.driver,
      dashboardEmbedded,
    }),
  );
  if (params.isBaas) {
    logger.log(
      `[server] lifecycle start (app=${process.env.GENCOW_APP_NAME || "unknown"}, startReason=${process.env.GENCOW_START_REASON || "unknown"}, restartId=${process.env.GENCOW_RESTART_ID || "unknown"})`,
    );
  }

  if (params.isBaas) {
    realtimeOutboxWorker?.start();
    startUsageMeteringFlush({
      isBaas: params.isBaas,
      meteringUrl: process.env.GENCOW_METERING_URL,
      runtimeBuffer: params.runtimeBuffer,
      fetchPlatformInternal: params.fetchPlatformInternal,
      internalToken: params.internalToken,
      appName: process.env.GENCOW_APP_NAME,
      appDbId: process.env.GENCOW_APP_DB_ID,
      logger,
    });
  }

  const shutdown = createShutdownHandler({
    isPlatform: params.isPlatform,
    isBaas: params.isBaas,
    functionsPath: params.functionsPath,
    env: process.env,
    runtimeBuffer: params.runtimeBuffer,
    fetchPlatformInternal: params.fetchPlatformInternal,
    internalToken: params.internalToken,
    readShutdownReasonFromMarker: params.readShutdownReasonFromMarker,
    close: async () => {
      await realtimeOutboxWakeUnsubscribe?.().catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);
        logger.warn(`[realtime-outbox] wake listener cleanup failed: ${message.slice(0, 160)}`);
      });
      await wakeSignalRuntime?.close().catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);
        logger.warn(`[wake-signal] App realtime listener close failed: ${message.slice(0, 160)}`);
      });
      await params.close();
    },
    logger,
  });
  registerShutdownSignals({
    processLike: process,
    shutdown,
  });

  return server;
}
